import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { MessageCircle, SendIcon, X } from 'lucide-react';
import toast from 'react-hot-toast';
import SellytLogo from '@/assets/svg/sellyt-logo.svg?react';
import { cn } from '@/lib/utils';
import { formatMessageTime } from '@/lib/helpers';
import { createLocalWelcomeMessage } from '@/lib/helpers/support-chat';
import { useSendSupportChatMessage, useSupportChatConversation } from '@/api';
import type { SupportChatMessage } from '@/types';
import {
  SUPPORT_CHAT_AGENT,
  SUPPORT_CHAT_QUICK_REPLIES,
  SUPPORT_CHAT_WELCOME,
} from './constants';
import { useAiSupportSocket } from './useAiSupportSocket';
import './support-chat-widget.css';

export interface SupportChatWidgetProps {
  /** Opens the existing “Ask Sellyt” ticket flow for human support. */
  onRequestHumanSupport?: () => void;
  className?: string;
}

const REPLY_TIMEOUT_MS = 60_000;

function TypingIndicator() {
  return (
    <div className="support-chat-typing inline-flex items-center gap-1 py-0.5">
      <span />
      <span />
      <span />
    </div>
  );
}

function ChatBubble({
  message,
  showName,
}: {
  message: SupportChatMessage;
  showName?: boolean;
}) {
  const isUser = message.sender === 'user';

  return (
    <div
      className={cn(
        'flex max-w-[88%] items-end gap-2',
        isUser && 'ml-auto flex-row-reverse max-w-[80%]'
      )}
    >
      {!isUser && (
        <div className="relative mb-[18px] flex size-8 shrink-0 items-center justify-center rounded-full border-2 border-[#012112] bg-gradient-to-br from-[#22B573] to-[#0a4128] text-[11px] font-bold text-white">
          S
          <span className="support-chat-status-dot absolute bottom-0 right-0 size-2.5 rounded-full border-2 border-[#012112] bg-[#00EB7B]" />
        </div>
      )}
      <div className="flex min-w-0 flex-col">
        {showName && !isUser && (
          <p className="mb-1 ml-3.5 text-[11px] font-semibold text-[#6B7280]">
            {SUPPORT_CHAT_AGENT.name}
          </p>
        )}
        <div
          className={cn(
            'rounded-2xl px-3.5 py-2.5 text-[13.5px] leading-relaxed shadow-sm',
            isUser
              ? 'rounded-tr-md bg-[#012112] text-white'
              : 'rounded-tl-md border border-[#EEF0F2] bg-white text-[#1A1A1A]'
          )}
        >
          {message.content}
        </div>
        <p
          className={cn(
            'mt-1 px-1.5 text-[10.5px] font-medium text-[#9CA3AF]',
            isUser && 'text-right'
          )}
        >
          {formatMessageTime(message.createdAt)}
        </p>
      </div>
    </div>
  );
}

export function SupportChatWidget({
  onRequestHumanSupport,
  className,
}: SupportChatWidgetProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [input, setInput] = useState('');
  const [conversationId, setConversationId] = useState<string | number | null>(
    null
  );
  const [messages, setMessages] = useState<SupportChatMessage[]>(() => [
    createLocalWelcomeMessage(SUPPORT_CHAT_WELCOME),
  ]);
  const [isTyping, setIsTyping] = useState(false);
  const [hasLoadedRemote, setHasLoadedRemote] = useState(false);

  const bodyRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const conversationIdRef = useRef<string | number | null>(null);
  const waitingForReplyRef = useRef<{
    correlationId?: string;
    timeoutId: number;
  } | null>(null);

  conversationIdRef.current = conversationId;

  const clearWaitingForReply = useCallback(() => {
    const waiting = waitingForReplyRef.current;
    if (waiting) {
      window.clearTimeout(waiting.timeoutId);
      waitingForReplyRef.current = null;
    }
    setIsTyping(false);
  }, []);

  const { data: remoteConversation, isLoading: isLoadingConversation } =
    useSupportChatConversation(isOpen);
  const { mutateAsync: sendMessage, isPending: isSending } =
    useSendSupportChatMessage();

  const handleAssistantReply = useCallback(
    (reply: SupportChatMessage, convId: string | number) => {
      const waiting = waitingForReplyRef.current;
      if (
        waiting?.correlationId &&
        reply.correlationId &&
        waiting.correlationId !== reply.correlationId
      ) {
        return;
      }

      setConversationId((current) => current ?? convId);
      setMessages((prev) => {
        if (prev.some((m) => String(m.id) === String(reply.id))) return prev;
        return [...prev, reply];
      });
      clearWaitingForReply();
    },
    [clearWaitingForReply]
  );

  useAiSupportSocket(isOpen, {
    conversationIdRef,
    onAssistantReply: handleAssistantReply,
  });

  useEffect(() => {
    return () => {
      const waiting = waitingForReplyRef.current;
      if (waiting) window.clearTimeout(waiting.timeoutId);
    };
  }, []);

  useEffect(() => {
    if (!isOpen || !remoteConversation || hasLoadedRemote) return;

    setConversationId(remoteConversation.id);
    if (remoteConversation.messages.length > 0) {
      setMessages(remoteConversation.messages);
    }
    setHasLoadedRemote(true);
  }, [isOpen, remoteConversation, hasLoadedRemote]);

  useEffect(() => {
    if (!isOpen) return;
    const el = bodyRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [isOpen, messages, isTyping]);

  useEffect(() => {
    if (isOpen) {
      window.setTimeout(() => inputRef.current?.focus(), 200);
    }
  }, [isOpen]);

  const unreadBadge = remoteConversation?.unreadCount ?? 0;

  const appendMessages = useCallback((...next: SupportChatMessage[]) => {
    setMessages((prev) => [...prev, ...next]);
  }, []);

  const sendUserMessage = useCallback(
    async (text: string) => {
      const trimmed = text.trim();
      if (!trimmed || isSending) return;

      const optimisticId = `pending-${Date.now()}`;
      const optimistic: SupportChatMessage = {
        id: optimisticId,
        content: trimmed,
        sender: 'user',
        createdAt: new Date().toISOString(),
      };

      appendMessages(optimistic);
      setInput('');
      setIsTyping(true);

      try {
        const result = await sendMessage({ message: trimmed });

        if (result.conversationId) {
          setConversationId(result.conversationId);
        }

        setMessages((prev) =>
          prev.map((m) => (m.id === optimisticId ? result.message : m))
        );

        const timeoutId = window.setTimeout(() => {
          waitingForReplyRef.current = null;
          setIsTyping(false);
          toast.error(
            'The assistant is taking longer than expected. Please try again.'
          );
        }, REPLY_TIMEOUT_MS);

        waitingForReplyRef.current = {
          correlationId: result.message.correlationId,
          timeoutId,
        };
      } catch (error) {
        clearWaitingForReply();
        setMessages((prev) => prev.filter((m) => m.id !== optimisticId));
        toast.error(
          error instanceof Error
            ? error.message
            : 'Could not send your message. Please try again.'
        );
      }
    },
    [appendMessages, clearWaitingForReply, isSending, sendMessage]
  );

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    void sendUserMessage(input);
  };

  const showQuickReplies = useMemo(
    () => messages.length <= 2 && !isTyping,
    [messages.length, isTyping]
  );

  return (
    <div className={cn('pointer-events-none', className)}>
      {isOpen && (
        <div
          className="support-chat-panel pointer-events-auto fixed bottom-24 right-6 z-[9990] flex h-[580px] max-h-[calc(100vh-120px)] w-[380px] max-w-[calc(100vw-32px)] flex-col overflow-hidden rounded-[20px] bg-white shadow-[0_1px_2px_rgba(0,0,0,0.06),0_24px_60px_rgba(1,33,18,0.28)] max-sm:inset-x-3 max-sm:bottom-[88px] max-sm:w-auto max-sm:max-w-none max-sm:h-[calc(100vh-112px)]"
          role="dialog"
          aria-label="Support chat"
        >
          <header className="relative overflow-hidden bg-gradient-to-br from-[#012112] via-[#073420] to-[#0a4128] px-[18px] pb-[18px] pt-3.5 text-left text-white">
            <div
              className="pointer-events-none absolute inset-0 opacity-50"
              style={{
                backgroundImage:
                  'radial-gradient(rgba(0,235,123,.16) 1.2px, transparent 1.2px)',
                backgroundSize: '18px 18px',
              }}
            />
            <div className="pointer-events-none absolute -right-[60px] -top-[60px] size-[200px] rounded-full bg-[radial-gradient(circle,rgba(0,235,123,.32),transparent_65%)] blur-[20px]" />

            <div className="relative z-[1] mb-3.5 flex items-center gap-2 pr-9">
              <SellytLogo className="h-[18px] w-auto brightness-0 invert opacity-95" />
              <span className="text-[11px] font-bold uppercase tracking-[0.08em] text-[#9af2c8]">
                Help &amp; Support
              </span>
            </div>

            <button
              type="button"
              className="absolute right-3.5 top-3.5 z-[2] flex size-[30px] cursor-pointer items-center justify-center rounded-full border-0 bg-white/10 text-white transition-colors hover:bg-white/20"
              onClick={() => setIsOpen(false)}
              aria-label="Close support chat"
            >
              <X className="size-3.5" />
            </button>

            <div className="relative z-[1] flex items-center gap-3">
              <div className="relative shrink-0">
                <div className="flex size-11 items-center justify-center rounded-full border-2 border-white/10 bg-gradient-to-br from-[#22B573] to-[#0a4128] text-sm font-bold">
                  S
                </div>
                <span className="support-chat-status-dot absolute bottom-0 right-0 size-2.5 rounded-full border-2 border-[#012112] bg-[#00EB7B]" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="flex items-center gap-2 text-base font-bold tracking-tight">
                  {SUPPORT_CHAT_AGENT.name}
                  <span className="support-chat-wave">👋</span>
                </p>
                <p className="mt-0.5 text-xs font-medium text-[#cfeede]">
                  {SUPPORT_CHAT_AGENT.role}
                </p>
                <p className="mt-1 flex items-center gap-1.5 text-[11.5px] font-medium text-[#9af2c8]">
                  <span className="support-chat-status-dot size-1.5 rounded-full bg-[#00EB7B]" />
                  {SUPPORT_CHAT_AGENT.status}
                </p>
              </div>
            </div>
          </header>

          <div
            ref={bodyRef}
            className="flex flex-1 flex-col gap-3.5 overflow-y-auto bg-[#FAFBFC] px-[18px] py-[18px] pb-2"
          >
            {isLoadingConversation && (
              <p className="text-center text-xs text-[#9CA3AF]">
                Loading chat…
              </p>
            )}

            {messages.map((message, index) => {
              const prev = index > 0 ? messages[index - 1] : null;
              const showName =
                message.sender !== 'user' && (!prev || prev.sender === 'user');

              return (
                <ChatBubble
                  key={String(message.id)}
                  message={message}
                  showName={showName}
                />
              );
            })}

            {isTyping && (
              <div className="flex max-w-[88%] items-end gap-2">
                <div className="mb-[18px] flex size-8 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-[#22B573] to-[#0a4128] text-[11px] font-bold text-white">
                  S
                </div>
                <div className="rounded-2xl rounded-tl-md border border-[#EEF0F2] bg-white px-3.5 py-2.5">
                  <TypingIndicator />
                </div>
              </div>
            )}

            {showQuickReplies && (
              <div className="flex flex-col gap-2 pl-11 pt-1">
                <p className="text-[11.5px] font-medium text-[#9CA3AF]">
                  Quick topics
                </p>
                {SUPPORT_CHAT_QUICK_REPLIES.map((label) => (
                  <button
                    key={label}
                    type="button"
                    className="cursor-pointer rounded-full border border-[#E4E7EA] bg-white px-3.5 py-2 text-left text-[12.5px] font-medium text-[#18854F] transition-colors hover:border-[#22B573] hover:bg-[#EAFFED] disabled:cursor-not-allowed disabled:opacity-50"
                    disabled={isSending}
                    onClick={() => void sendUserMessage(label)}
                  >
                    {label}
                  </button>
                ))}
              </div>
            )}
          </div>

          {onRequestHumanSupport && (
            <div className="flex items-center justify-between gap-2.5 border-t border-[#EEF0F2] bg-[#FAFBFC] px-3.5 py-2.5">
              <button
                type="button"
                className="inline-flex cursor-pointer items-center gap-2 rounded-full border border-[#C8F3D2] bg-[#EAFFED] px-3 py-1.5 text-[12.5px] font-semibold text-[#18854F] transition-colors hover:border-[#22B573] hover:bg-[#DCF5E1]"
                onClick={() => {
                  setIsOpen(false);
                  onRequestHumanSupport();
                }}
              >
                Talk to support team
              </button>
              <span className="text-[11px] font-medium text-[#9CA3AF]">
                Ticket via email
              </span>
            </div>
          )}

          <form
            onSubmit={handleSubmit}
            className="flex items-center gap-2 border-t border-[#EEF0F2] bg-white px-3.5 py-3"
          >
            <input
              ref={inputRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Type your message…"
              disabled={isSending}
              className="h-[38px] min-w-0 flex-1 rounded-full border-0 bg-[#F4F5F7] px-3 text-[13.5px] font-medium text-[#1A1A1A] outline-none placeholder:text-[#9CA3AF] focus:bg-[#EAEDF1] disabled:opacity-60"
            />
            <button
              type="submit"
              disabled={!input.trim() || isSending}
              className="flex size-9 shrink-0 cursor-pointer items-center justify-center rounded-full border-0 bg-[#22B573] text-white transition-colors hover:bg-[#1E9F66] disabled:cursor-not-allowed disabled:opacity-50"
              aria-label="Send message"
            >
              <SendIcon className="size-4" />
            </button>
          </form>
        </div>
      )}

      <button
        type="button"
        className={cn(
          'pointer-events-auto fixed bottom-6 right-6 z-[9991] flex size-[60px] cursor-pointer items-center justify-center rounded-full border-0 text-[26px] shadow-[0_1px_2px_rgba(0,0,0,0.06),0_14px_32px_rgba(1,33,18,0.32)] transition-all duration-200 hover:-translate-y-0.5 hover:shadow-[0_1px_2px_rgba(0,0,0,0.06),0_18px_40px_rgba(1,33,18,0.42)] active:translate-y-0 max-sm:bottom-4 max-sm:right-4',
          isOpen
            ? 'border border-[#E4E7EA] bg-white text-[#1A1A1A]'
            : 'bg-[#012112] text-[#00EB7B]'
        )}
        onClick={() => setIsOpen((open) => !open)}
        aria-label={isOpen ? 'Close support chat' : 'Open support chat'}
        aria-expanded={isOpen}
      >
        {isOpen ? (
          <X className="size-[18px]" />
        ) : (
          <MessageCircle className="size-[26px]" strokeWidth={2} />
        )}
        {!isOpen && unreadBadge > 0 && (
          <span className="absolute right-1.5 top-1.5 flex min-h-[18px] min-w-[18px] items-center justify-center rounded-full border-2 border-[#012112] bg-[#FB0000] px-1 text-[11px] font-bold text-white">
            {unreadBadge > 9 ? '9+' : unreadBadge}
          </span>
        )}
      </button>
    </div>
  );
}
